import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import FeedbackTabs, { TABS, resolveTab } from './FeedbackTabs';
import FirebaseFeedbackBody from '../firebase/FirebaseFeedbackBody';
import SurveyResponsesBody from '../survey/SurveyResponsesBody';
import SignupSurveyBody from '../signup-survey/SignupSurveyBody';
import CeoRequestsBody from '../ceo-requests/CeoRequestsBody';
import '../active-users/active-users.css';
import './user-feedback.css';

/**
 * User Feedback — everything members tell us, on one screen (feature 067-user-feedback-tabs).
 *
 * Replaces four sidebar entries and four screens with one entry and a tab per source. Each source
 * keeps its own API, filters and columns; this component owns only the shell, the tab strip and
 * which body is showing. `FeedbackTabs.TABS` is the single list — add or remove a tab there and
 * here, never anywhere else.
 *
 * ── TWO BEHAVIOURS THIS COMPONENT EXISTS TO GET RIGHT ────────────────────────────────────────
 *
 * 1. THE TAB LIVES IN THE URL (`?tab=`). A tab you cannot link to or refresh into is not a
 *    location, it is a mode — and an admin who reloads after ten minutes of filtering would land
 *    somewhere else. An unknown value resolves to the default rather than erroring (FR-005).
 *
 * 2. TABS MOUNT LAZILY AND THEN STAY MOUNTED. Mounting them all eagerly would fire every tab's
 *    requests on open (FR-006). Unmounting on switch would throw away that tab's filters, page
 *    and scroll (FR-007). Mount on first visit, hide with `hidden` thereafter, and both hold —
 *    without either body knowing the other exists.
 */

const BODIES = {
  firebase: FirebaseFeedbackBody,
  survey: SurveyResponsesBody,
  'signup-survey': SignupSurveyBody,
  ceo: CeoRequestsBody,
};

export default function UserFeedback() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = resolveTab(searchParams.get('tab'));

  // Which tabs have ever been opened. A tab is rendered from the moment it enters this set and
  // is never removed from it — that is the whole of the "lazy but persistent" behaviour.
  const [visited, setVisited] = useState(() => new Set([active]));

  const selectTab = (id) => {
    setVisited((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    // `replace` so a run of tab clicks does not bury the page the admin arrived from under a
    // dozen history entries they have to press Back through.
    setSearchParams({ tab: id }, { replace: true });
  };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Feedback › <b>User Feedback</b></span>
            <span className="au-route">/user-feedback</span>
            <span className="au-spacer" style={{ flex: 1 }} />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>

          <h1 className="au-title">User Feedback</h1>
          <p className="uf-subtitle">
            Everything members have told us, from every source, in one place.
          </p>

          <FeedbackTabs active={active} onSelect={selectTab} />

          {TABS.map((t) => {
            const Body = BODIES[t.id];
            if (!visited.has(t.id)) return null;
            return (
              <div
                key={t.id}
                id={`uf-panel-${t.id}`}
                role="tabpanel"
                aria-labelledby={`uf-tab-${t.id}`}
                className="uf-panel"
                hidden={t.id !== active}
              >
                <Body />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
